// ────────────────────────────────────────────────────────────────
// Participant Chart Entries API (Day 2 — Bowel Chart)
// Requires both charts_module_enabled AND bowel_chart_enabled on backend.
// ────────────────────────────────────────────────────────────────

import api from '@/lib/api';

export async function listEntries(participantId, chartId, { status, limit = 200 } = {}) {
  const params = { limit };
  if (status) params.status = status;
  const res = await api.get(`/participants/${participantId}/charts/${chartId}/entries`, { params });
  return res.data?.data || [];
}

export async function createEntry(participantId, chartId, payload) {
  const res = await api.post(`/participants/${participantId}/charts/${chartId}/entries`, payload);
  return res.data?.data;
}

export async function getEntry(participantId, chartId, entryId) {
  const res = await api.get(`/participants/${participantId}/charts/${chartId}/entries/${entryId}`);
  return res.data?.data;
}

export async function updateEntry(participantId, chartId, entryId, payload) {
  const res = await api.put(`/participants/${participantId}/charts/${chartId}/entries/${entryId}`, payload);
  return res.data?.data;
}

export async function archiveEntry(participantId, chartId, entryId) {
  const res = await api.delete(`/participants/${participantId}/charts/${chartId}/entries/${entryId}`);
  return res.data?.data;
}

// Slice D — NDIS-reportable acknowledge (non-blocking compliance step)
export async function acknowledgeNdisEntry(participantId, chartId, entryId, note) {
  const res = await api.post(
    `/participants/${participantId}/charts/${chartId}/entries/${entryId}/ndis-acknowledge`,
    note ? { note } : {},
  );
  return res.data?.data;
}

// Slice D Step 2 — private chart photo upload + gated retrieval
export async function uploadChartPhoto(participantId, chartId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(
    `/participants/${participantId}/charts/${chartId}/photos`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data?.data; // { url, mime, uploaded_at }
}

export async function fetchChartPhotoBlob(photoUrl) {
  // Stored urls look like "/api/participants/.../photos/{id}"; the axios client
  // baseURL already includes "/api", so strip it before requesting the blob.
  const path = photoUrl.startsWith('/api/') ? photoUrl.slice(4) : photoUrl;
  const res = await api.get(path, { responseType: 'blob' });
  return res.data;
}

// Enums
export const SHIFT_PERIODS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
];

export const STOOL_TYPES = [
  { value: 'bristol_1', label: 'Bristol 1 — Separate hard lumps' },
  { value: 'bristol_2', label: 'Bristol 2 — Lumpy sausage' },
  { value: 'bristol_3', label: 'Bristol 3 — Sausage, cracked' },
  { value: 'bristol_4', label: 'Bristol 4 — Smooth sausage' },
  { value: 'bristol_5', label: 'Bristol 5 — Soft blobs' },
  { value: 'bristol_6', label: 'Bristol 6 — Fluffy pieces' },
  { value: 'bristol_7', label: 'Bristol 7 — Entirely liquid' },
];

export const AMOUNTS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const CONTINENCE = [
  { value: 'continent', label: 'Continent' },
  { value: 'incontinent', label: 'Incontinent' },
  { value: 'assisted', label: 'Assisted' },
];

export const SHORT_STOOL_LABEL = {
  bristol_1: 'B1', bristol_2: 'B2', bristol_3: 'B3', bristol_4: 'B4',
  bristol_5: 'B5', bristol_6: 'B6', bristol_7: 'B7',
};
